import React, { useCallback, useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"

const DEFAULT_ROW_HEIGHT = 52
const OVERSCAN = 6

const SimpleVirtualList = ({
  items,
  rowHeight = DEFAULT_ROW_HEIGHT,
  maxHeight = 600,
  renderRow,
  footer,
  className = "",
}) => {
  const containerRef = useRef(null)
  const [range, setRange] = useState({ start: 0, end: 30 })

  const updateRange = useCallback(() => {
    const el = containerRef.current
    if (!el || !items?.length) return
    const scrollTop = el.scrollTop
    const viewHeight = el.clientHeight
    const start = Math.max(0, Math.floor(scrollTop / rowHeight) - OVERSCAN)
    const visibleCount = Math.ceil(viewHeight / rowHeight) + OVERSCAN * 2
    const end = Math.min(items.length, start + visibleCount)
    setRange((prev) =>
      prev.start === start && prev.end === end ? prev : { start, end }
    )
  }, [items?.length, rowHeight])

  useEffect(() => {
    updateRange()
  }, [items, updateRange])

  if (!items?.length) {
    return null
  }

  const totalHeight = items.length * rowHeight
  const slice = items.slice(range.start, range.end)

  return (
    <div className={className}>
      <div
        ref={containerRef}
        className="overflow-y-auto"
        style={{ maxHeight }}
        onScroll={updateRange}
      >
        <div style={{ height: totalHeight, position: "relative" }}>
          {slice.map((item, index) => {
            const absoluteIndex = range.start + index
            const row = renderRow(item, absoluteIndex)
            if (row == null || row === false) {
              return null
            }
            return (
              <div
                key={item.key ?? absoluteIndex}
                style={{
                  position: "absolute",
                  top: absoluteIndex * rowHeight,
                  left: 0,
                  right: 0,
                  height: rowHeight,
                }}
              >
                {row}
              </div>
            )
          })}
        </div>
      </div>
      {footer ? <div className="mt-2">{footer}</div> : null}
    </div>
  )
}

SimpleVirtualList.propTypes = {
  items: PropTypes.array,
  rowHeight: PropTypes.number,
  maxHeight: PropTypes.number,
  renderRow: PropTypes.func.isRequired,
  footer: PropTypes.node,
  className: PropTypes.string,
}

export default SimpleVirtualList
